<template>
  <teleport to="body">
    <transition name="st-modal">
      <div
        v-if="open"
        class="st-modal-backdrop"
        @click.self="onBackdrop"
        @keydown.esc="emit('close')"
      >
        <div
          ref="panel"
          class="st-modal"
          :class="`st-modal--${size}`"
          role="dialog"
          aria-modal="true"
          tabindex="-1"
        >
          <header v-if="title || $slots.header" class="st-modal__header">
            <slot name="header">
              <h2 class="st-modal__title">{{ title }}</h2>
            </slot>
            <StIconButton
              v-if="dismissible"
              icon="xmark"
              label="Close"
              size="sm"
              @click="emit('close')"
            />
          </header>
          <div class="st-modal__body">
            <slot />
          </div>
          <footer v-if="$slots.footer" class="st-modal__footer">
            <slot name="footer" />
          </footer>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup lang="ts">
import { ref, watch, nextTick } from 'vue';
import StIconButton from './StIconButton.vue';

const props = withDefaults(
  defineProps<{
    open: boolean;
    title?: string;
    size?: 'sm' | 'md' | 'lg';
    /** Clicking the backdrop emits close. */
    closeOnBackdrop?: boolean;
    /** Show the header close (X) button. */
    dismissible?: boolean;
  }>(),
  { size: 'sm', closeOnBackdrop: true, dismissible: true },
);

const emit = defineEmits<{ close: [] }>();
const panel = ref<HTMLElement | null>(null);

function onBackdrop() {
  if (props.closeOnBackdrop) emit('close');
}

watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) return;
    // Focus the first text field inside the modal (e.g. the name input of an
    // "Add …" dialog) so it's ready to type into; fall back to the panel itself.
    nextTick(() => {
      const el = panel.value;
      if (!el) return;
      const field = el.querySelector<HTMLElement>('input, textarea, select, [autofocus]');
      (field ?? el).focus();
    });
  },
);
</script>

<style scoped>
.st-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: var(--st-z-modal-backdrop);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--st-space-4);
  background: rgba(15, 17, 21, 0.55);
  backdrop-filter: blur(2px);
}
.st-modal {
  z-index: var(--st-z-modal);
  width: 100%;
  max-width: 24rem;
  background: var(--st-surface);
  border-radius: var(--st-radius-lg);
  box-shadow: var(--st-shadow-3);
  outline: none;
  overflow: hidden;
}
.st-modal--md {
  max-width: 32rem;
}
.st-modal--lg {
  max-width: 44rem;
}
.st-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--st-space-3);
  padding: var(--st-space-4) var(--st-space-5);
  border-bottom: 1px solid var(--st-border);
}
.st-modal__title {
  font-size: var(--st-text-lg);
  font-weight: var(--st-weight-semibold);
  color: var(--st-text);
}
.st-modal__body {
  padding: var(--st-space-5);
  font-size: var(--st-text-md);
  color: var(--st-text-muted);
}
.st-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--st-space-2);
  padding: var(--st-space-4) var(--st-space-5);
  border-top: 1px solid var(--st-border);
  background: var(--st-surface-sunken);
}

.st-modal-enter-active,
.st-modal-leave-active {
  transition: opacity var(--st-transition-base) var(--st-ease);
}
.st-modal-enter-active .st-modal,
.st-modal-leave-active .st-modal {
  transition: transform var(--st-transition-base) var(--st-ease);
}
.st-modal-enter-from,
.st-modal-leave-to {
  opacity: 0;
}
.st-modal-enter-from .st-modal,
.st-modal-leave-to .st-modal {
  transform: translateY(8px) scale(0.98);
}
</style>
